
"use client";
import axios from "axios";
import { store } from "../redux/store";
import { validateAndFetchToken } from "./actions/tokenService";
import { getAnonymousId } from "./anonymousId";
import { getCustomerPosition } from "./customerPosition";

const baseUrl = `${process.env.NEXT_PUBLIC_API_BASE_URL}`;

// Short-lived dedup for the search endpoint ONLY. The home search form and the
// /search page fire the *same* search/index request within a few hundred ms of
// each other (the form fetches to decide navigation, then /search re-fetches on
// mount), which doubled Elasticsearch load and inflated the search log (~1.44x
// on web). search/index is an idempotent read, so collapsing identical
// concurrent / near-simultaneous calls is safe and hands the same result to
// every caller. Scoped to search/index and nothing else — it must never touch
// mutations (cart, otp, booking), where a second identical call is a distinct
// intent that has to go through.
const SEARCH_DEDUP_URL = "search/index";
const SEARCH_DEDUP_TTL_MS = 2000;
const _searchInflight = new Map(); // key -> Promise<result>  (in-flight sharing)
const _searchRecent = new Map(); // key -> { result, at }    (just-completed cache)

const runRequest = async (apiparams) => {
  try {
    await store.dispatch(validateAndFetchToken());
  } catch {
    // Token fetch failed — continue with existing/empty token
  }
  const { initToken } = store.getState().config;
  const anonymousId = getAnonymousId();

  try {
    const response = await axios({
      url: `${baseUrl}/${apiparams.url}`,
      method: apiparams.method,
      headers: {
        Authorization: `Bearer ${initToken}`,
        "Content-Type": "application/json",
        // Survives login (unlike the token above), so the server can attribute
        // pre-login activity once this person identifies themselves. Omitted
        // entirely when unavailable — never sent as an empty string.
        ...(anonymousId ? { "X-Anonymous-Id": anonymousId } : {}),
      },
      params: apiparams.quryParams,
      data: apiparams.data || undefined,
      validateStatus: function (status) {
        // Accept all status codes (including 409 for collision detection)
        return status >= 200 && status < 600;
      },
    });

    const body = response.data;
    return {
      success: body?.status == 1,
      data: body,
      message: body?.error || body?.msg || body?.message || null,
      httpStatus: response.status,
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      message: error?.message || "Network error. Please try again.",
      httpStatus: null,
    };
  }
};

/**
 * Binary GET (currently the invoice PDF). runRequest parses every response as
 * JSON, which corrupts a PDF, so this takes the same auth path and asks axios
 * for a blob instead.
 */
export const downloadFileFromApi = async ({ url, quryParams, filename }) => {
  try {
    await store.dispatch(validateAndFetchToken());
  } catch {
    // Token fetch failed — continue with existing/empty token
  }
  const { initToken } = store.getState().config;

  try {
    const response = await axios({
      url: `${baseUrl}/${url}`,
      method: "GET",
      headers: { Authorization: `Bearer ${initToken}` },
      params: quryParams,
      responseType: "blob",
    });

    // An error response still arrives as a blob; it is JSON, not a PDF.
    if (response.data?.type === "application/json") {
      return { success: false, message: "Could not generate the invoice. Please try again." };
    }

    const objectUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
    return { success: true, message: null };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.status === 403
        ? "You are not authorised to view this invoice."
        : "Could not download the invoice. Please try again.",
    };
  }
};

const pruneRecent = (now) => {
  // The window is 2s, so this map only ever holds a handful of entries; prune
  // the expired ones on write so it can't grow across a long session.
  for (const [k, v] of _searchRecent) {
    if (now - v.at >= SEARCH_DEDUP_TTL_MS) _searchRecent.delete(k);
  }
};

export const usefetchDataFromApi = async (apiparams) => {
  // Everything except the search read passes straight through, so mutations are
  // never collapsed.
  if (apiparams.url !== SEARCH_DEDUP_URL) {
    return runRequest(apiparams);
  }

  // Attach the customer's position HERE, not at the four call sites that build
  // a search payload. The dedup below keys on the exact JSON body, so a payload
  // carrying coordinates from one call site and not another yields two
  // different keys — the home-form/search-page pair stops collapsing, and the
  // two calls return different answers for the same search. One injection point
  // keeps every payload identical.
  //
  // Purely additive: the server treats the position as optional and returns the
  // same vehicles either way, annotated with a road distance per pickup point.
  const position = getCustomerPosition();
  if (position) {
    apiparams = {
      ...apiparams,
      data: { ...(apiparams.data || {}), customerLat: position.lat, customerLng: position.lng },
    };
  }

  const key = JSON.stringify(apiparams.data || {});
  const now = Date.now();

  // A just-completed identical search — reuse its result. This is what absorbs
  // the form-then-mount re-fetch (the two land a few hundred ms apart).
  const recent = _searchRecent.get(key);
  if (recent && now - recent.at < SEARCH_DEDUP_TTL_MS) {
    return recent.result;
  }

  // An identical search already in flight — share its promise instead of firing
  // a second network call.
  const inflight = _searchInflight.get(key);
  if (inflight) return inflight;

  const promise = runRequest(apiparams)
    .then((result) => {
      const at = Date.now();
      pruneRecent(at);
      _searchRecent.set(key, { result, at });
      return result;
    })
    .finally(() => {
      _searchInflight.delete(key);
    });

  _searchInflight.set(key, promise);
  return promise;
};
